import { Rule } from '@travelnest/merlin.rule';
import { useState } from 'react';
import * as React from 'react';
import { IImage } from '../../../../api/images';
import { IItemTypes } from '../../../../data//entities/property/itemTypes';
import { IProperty } from '../../../../data/entities/property';
import { IBed } from '../../../../data/entities/property/bed';
import { Channel, channels } from '../../../../entities/contracts/constants';
import { AccordionHeader, AccordionItem } from '../../../../view/components/accordion';
import { BackButton } from '../../../../view/components/back-button/BackButton';
import { FormInput } from '../../../../view/components/form';
import { Heading } from '../../../../view/components/heading/Heading';
import { Loader } from '../../../../view/components/loader/Loader';
import { Content } from '../../../../view/components/page';
import { Paragraph } from '../../../../view/components/paragraph/Paragraph';
import { PhotoWall } from '../../../../view/components/tile-wall/PhotoWall';
import { bedsToRooms, buildSubheading, calcFloorSpace } from './bookingcom-helper';
import photoTags from './photoTags';

export interface IBookingComHelperProps {
	loading: boolean;
	property: IProperty | undefined;
	photos: IImage[] | undefined;
	itemTypes: IItemTypes | undefined;
}

const BOOKING_COM_LOGO = channels.find((c) => c.id === Channel.BOOKINGCOM);

const loader = () => <><Loader loading={true} /></>;

const typeDisplayName = (bedType: string) => bedType[0].toUpperCase() + bedType.slice(1);

const groupBeds = (beds: IBed[]): { [key: string]: number } => {
	return beds.reduce((grouped, bed) => ({ ...grouped, [bed.bed_type]: (grouped[bed.bed_type] || 0) + 1 }), {});
};

const bedSummary = (beds: IBed[]) => {
	const grouped = groupBeds(beds);
	const names = Object.entries(grouped).map(([key, value]) => `${value} ${typeDisplayName(key)}${value > 1 ? 's' : ''}`);
	return names.join(', ');
};

export const BookingComHelper: React.FunctionComponent<IBookingComHelperProps> = ({ loading, property, photos, itemTypes }) => {

	const [tagSearch, setTagSearch] = useState('');

	const filteredTags = photoTags.filter((t) => {
		const parts = t.split('.');
		return parts[parts.length - 1].toLowerCase().includes(tagSearch.toLowerCase());
	});

	if (loading) return loader();

	if (!property) {
		return <strong>No property 🤷‍♀️</strong>;
	}

	const internetAmenity = property.amenities.find((a) => a.amenity_type === 'internet');
	const parkingAmenity = property.amenities.find((a) => a.amenity_type === 'parking');
	const gardenAmenity = property.amenities.find((a) => a.amenity_type === 'garden');
	const childrenRestriction = property.restrictions.find((r) => r.restriction_type === 'children allowed');
	const cleaningFeeRestriction = property.restrictions.find((r) => r.restriction_type === 'cleaning fee');
	const groupBookingRestriction = property.restrictions.find((r) => r.restriction_type === 'group bookings');
	const petsRestriction = property.restrictions.find((r) => r.restriction_type === 'pets');
	const smokingRestriction = property.restrictions.find((r) => r.restriction_type === 'smoking');

	const bedrooms = property ? bedsToRooms(property.beds) : null;

	return <div style={{display: 'flex'}}>
		{loading ?
			<Loader loading={true} /> : !property ? 'No property found' : <React.Fragment>
				<div className="">
					<Content type="full">
						<div className="">
							<BackButton to="/admin" />
							<div style={{ backgroundColor: BOOKING_COM_LOGO?.primaryColor ?? 'white' }}>
								<img style={{ padding: '10px' }} src={BOOKING_COM_LOGO?.whiteLogo} />
							</div>
						</div>
						<h2>Listing Helper ({property.id})</h2>
						<h3>{property.name}</h3>
						<i>Not that you cannot change anything from here. The buttons are left for speed, FNP 😅</i>
						<AccordionHeader collapsible={true}>Policies</AccordionHeader>
						<AccordionItem>
							<Heading type={'D1'}>Pre-authorization preferences</Heading>
							<Paragraph fillParent="mid">
								Set to <b>No</b>
							</Paragraph>
							<Rule />

							<Heading type={'D1'}>Children</Heading>
							<Paragraph fillParent="mid">
								This property <b>{childrenRestriction && childrenRestriction.allowed ? 'allows' : 'does not allow'} children</b>
							</Paragraph>
							<Rule />

							<Heading type={'D1'}>Guest Payment Options</Heading>
							<Paragraph fillParent="mid">
								If payment is set to <b>Cash</b>:
							</Paragraph>
							<Paragraph fillParent="mid">
								Change to allow credit cards: Amex, Maestro/Eurocard, MasterCard, Visa
							</Paragraph>
							<Paragraph fillParent="mid">
								Set "Allow guests to pay online" to <b>Yes</b>
							</Paragraph>
							<Rule />

							<Heading type={'D1'}>Internet and Parking:</Heading>
							<Paragraph fillParent="mid">
								This property has <b>{internetAmenity && internetAmenity.amenity_subtype === 'wifi' ? 'Wi-Fi' : 'No Wi-Fi'}</b>
							</Paragraph>
							<Paragraph fillParent="mid">
								This property has <b>{parkingAmenity && parkingAmenity.amenity_subtype === 'on site' ? 'Onsite parking' : 'No onsite parking'}</b>
							</Paragraph>
							<Rule />

							<Heading type={'D1'}>Guest Information:</Heading>
							<Paragraph fillParent="mid">
								Check-in is: <b>{property.check_in}</b>
							</Paragraph>
							<Paragraph fillParent="mid">
								Check-out is: <b>{property.check_out}</b>
							</Paragraph>
							<Rule />

							<Heading type={'D1'}>Additional Fees & Charges:</Heading>
							<Paragraph fillParent="mid">
								Damage deposit is <b>{property.security_deposit}</b>
							</Paragraph>
							<Paragraph fillParent="mid">
								{/* tslint:disable-next-line */}
								This property has <b>{cleaningFeeRestriction && !!cleaningFeeRestriction.charge ? `has a ${cleaningFeeRestriction.charge}` : 'does not have a'} cleaning-fee</b>
							</Paragraph>
							<Rule />

							<Heading type={'D1'}>Key Pickup:</Heading>
							<Paragraph fillParent="mid">
								Set "How do guests enter your property?" to <b>We'll send instructions</b>
							</Paragraph>
							<Paragraph fillParent="mid">
								Set "How will you send instructions to your guests?" to <b>By e-mail</b>
							</Paragraph>
							<Paragraph fillParent="mid">
								Set "When will you send instructions" to <b>A week before the guest arrives up until the day of arrival</b>
							</Paragraph>
							<Rule />

							<Heading type={'D1'}>House Rules:</Heading>
							<Paragraph fillParent="mid">
								{/* tslint:disable-next-line */}
								This property <b>{smokingRestriction && smokingRestriction.allowed ? 'allows' : 'does not allow'} smoking</b>
							</Paragraph>
							<Paragraph fillParent="mid">
								{/* tslint:disable-next-line */}
								This property <b>{groupBookingRestriction && groupBookingRestriction.allowed ? 'allows' : 'does not allow'} parties</b>
							</Paragraph>
							<Paragraph fillParent="mid">
								{/* tslint:disable-next-line */}
								This property <b>{petsRestriction && petsRestriction.allowed ? 'allows' : 'does not allow'} pets</b>
							</Paragraph>
							<Rule />

						</AccordionItem>

						<AccordionHeader collapsible={true}>Photos</AccordionHeader>
						<AccordionItem>
							<PhotoWall
								images={photos || []}
								disabled={[]}
								onDelete={(_) => null}
								onView={(_) => null}
								onReorder={(_) => null}
							/>
							<Rule />
							<Heading type={'D1'}>Download each photo in <i>FULL</i> resolution: </Heading>
							{(photos || []).map((p, i) => <Paragraph fillParent="mid">
								<a href={p.formats[p.original].url} download target="_blank">Download photo {i + 1}</a>
							</Paragraph>)}

						</AccordionItem>

						<AccordionHeader collapsible={true}>Photo Tag Helper</AccordionHeader>
						<AccordionItem>
							<Heading type={'D1'}>Use this to <i>search</i> for tags!</Heading>
							<FormInput
								onChange={(e) => setTagSearch(e.target.value)}
								type="text"
								name="photo"
								id="photo"
								value={tagSearch} />
							<div>
								{filteredTags.map((t) => <p>{t.split('.').join(' → ')}</p>)}
							</div>
						</AccordionItem>

						<AccordionHeader collapsible={true}>Property Layout</AccordionHeader>
						<AccordionItem>
							<Heading type={'D1'}>Property Layout:</Heading>
							<Paragraph fillParent="mid">
								Set "Number of apartments (of this type)" to <b>1</b>
							</Paragraph>
							<Paragraph fillParent="mid">
								Set "Number of living rooms" to <b>1</b> unless it is <i>obviously not right</i>
							</Paragraph>
							<Paragraph fillParent="mid">
								Number of floors in property = {property.number_of_floors}
							</Paragraph>
							<Paragraph fillParent="mid">
								entrance is of floor = {property.entrance_floor}
							</Paragraph>
							<Paragraph fillParent="mid">
								elevator = {property.has_elevator ? 'Yes' : 'No'}
							</Paragraph>

							<Rule />

							<Heading type={'D1'}>Bed Options:</Heading>
							<Paragraph fillParent="mid">
								{/* If loading is false then we have bedrooms */}
								This property has <b>{bedrooms!.length} bedrooms</b>
							</Paragraph>
							<ul>
								{bedrooms!.map((bedArray, i) => <div>
									{`Bedroom ${(i + 1)} - ${bedSummary(bedArray)}`}
									<ul>
										{bedArray.map((bed, j) => <li key={j}>{bed.bed_type}</li>)}
									</ul>
									<Rule />
								</div>)}
							</ul>
							<Rule />

							<Heading type={'D1'}>Bathroom Options:</Heading>
							<Paragraph fillParent="mid">
								This property has <b>{property.bathrooms.length} bathrooms</b>
							</Paragraph>
							{property.bathrooms.map((b, i) => <div>
								<Heading type={'D1'}>Bathroom {i + 1}</Heading>
								<Paragraph fillParent="mid">
									{buildSubheading(b)}
								</Paragraph>
							</div>)}
							<Rule />
						</AccordionItem>

						<AccordionHeader collapsible={true}>Room Amenities</AccordionHeader>
						<AccordionItem>
							<Heading type={'D1'}>Floor Space</Heading>
							<Paragraph fillParent="mid">This property is <b>{property.property_size} m^2</b></Paragraph>
							<Paragraph fillParent="mid">Calculated Floor Space <i>(not stored)</i>: <b>{calcFloorSpace(property, bedrooms!)} m^2</b></Paragraph>
							<Rule />
							<Heading type={'D1'}>The property has the following amenities</Heading>
							{property.amenities.sort((a, b) => a.amenity_type.localeCompare(b.amenity_type)).map((a) => <Paragraph fillParent="mid">
								{a.amenity_type} {a.amenity_subtype && a.amenity_subtype !== 'default' ? `→ ${a.amenity_subtype}` : ''}
							</Paragraph>)}
							<Paragraph fillParent="mid">You should add <b>Toilet Paper 🧻</b></Paragraph>
						</AccordionItem>

						<AccordionHeader collapsible={true}>Facilities & Services</AccordionHeader>
						<AccordionItem>
							<Heading type={'D1'}>Top Facilities</Heading>
							{smokingRestriction && !smokingRestriction.allowed && <Paragraph fillParent="mid">
								Set non-smoking rooms to <b>Yes</b> because smoking is not allowed</Paragraph>}
							{gardenAmenity && <Paragraph fillParent="mid">
								Set garden to <b>Yes</b> because the property has a garden</Paragraph>}
							<Rule />
							<Heading type={'D1'}>Languages Spoken</Heading>
							<Paragraph fillParent="mid">
								Set to <b>English</b>
							</Paragraph>
							<Rule />
						</AccordionItem>

						<AccordionHeader collapsible={true}>How to get here/ What's nearby?</AccordionHeader>
						<AccordionItem>
							<i>You can use the address and description to help you fill this out</i>
							<Heading type={'D1'}>Property Address</Heading>
							<Paragraph fillParent="mid">Address 1: {property.address1}</Paragraph>
							<Paragraph fillParent="mid">Address 2: {property.address2}</Paragraph>
							<Paragraph fillParent="mid">City: {property.city}</Paragraph>
							<Paragraph fillParent="mid">Postcode: {property.postcode}</Paragraph>
							<Paragraph fillParent="mid">Country: {property.country_code}</Paragraph>
							<Rule />
							<Heading type={'D1'}>Description</Heading>
							{property.long_description.split('\n').map((part) => <Paragraph>{part}</Paragraph>)}
							<Rule />
						</AccordionItem>
						<Rule />
					</Content>
				</div>
			</React.Fragment>
		}
	</div>;
};
